<template lang="pug">
.zendesk-form.main-container(ref="root")
  .form(v-if="status !== 'success' && fields.length > 0")
    div.formFieldContainer(
      v-for="field in activeFields"
    )
      div.formMessage(
        v-if="field.component === 'div'"
        v-html="field.description"
      )
      .drag-and-drop(v-else-if="field.component === 'FileUpload'")
        .uploads
          ol.files
            li(v-if="field.files" v-for="(file, index) in field.files") {{ file.name }}
              RogueButton(@click="removeFile(index, field.id)" :text="__('Remove File')" :color="'link-black'")
        DragAndDropFileUpload(
          :button-text="'Attach a File'"
          :loading="false"
          :show-button="false"
          :accept="'.jpg,.jpeg,.png,.gif,.tiff,.doc,.docx,.pdf,.mpg,.mov,.wmv,.xls,.xlsx'"
          @fileUpload="attachFile($event, field)"
        )
      component.formField(
        v-else
        v-show="!field.hide"
        :is="mapComponentName(field.component)"
        :label="field.label"
        :name="field.name"
        :type="VALID_INPUTS.includes(field.component) ? field.type : undefined"
        :options="field.options"
        :modelValue="field.value"
        :required="field.required"
        :disabledOptionText="__('Select an option ...')"
        :errorDisplay="field.errorDisplay"
        :disabled="field.readOnly"
        @update:modelValue="updateFieldValue(field, $event)"
      )
    RogueButton.submit(:loading="loading" :disabled="disabled" @click="submit()" :text="__('Submit')")
  Message(v-if="status" :status="['commonError','fileError','fileSizeError'].includes(status) ? 'error' : status")
    .message(v-html="__(statusMessage[status])")
</template>

<script setup lang="ts">
import { nextTick, onMounted, reactive, ref, useTemplateRef } from 'vue'
import { useRogue } from '../../js/rogue'
import type { FormField, ZendeskFile } from './zendeskTypes'

const PERSISTENT_ENTRIES: {
  [key: string]: { id: number, value?: string | boolean }[]
} = {}

const props = withDefaults(defineProps<{
  id: string,
  prePopulate?: object,
  successMessage?: string,
  warningMessage?: string,
  commonErrorMessage?: string,
  fileErrorMessage?: string,
  fileSizeErrorMessage?: string,
}>(), {
  prePopulate          : () => ({}),
  successMessage       : '<p>Success!<br><br> Thank you for contacting us.<br><br> A Rogue team member will be in contact with you soon.</p>',
  warningMessage       : 'Please fill in the required fields',
  commonErrorMessage   : 'Failed to send your message. Please try again later.',
  fileErrorMessage     : 'There was an issue loading your file. You may upload images and mp4s less than 20mb',
  fileSizeErrorMessage : 'This file is too large, upload another file or submit without file',
})

const rogue = useRogue()

const rootEl = useTemplateRef<HTMLElement>('root')
const fields = ref<FormField[]>([])
const activeFields = ref<FormField[]>([])
const loading       = ref<boolean>(false)
const status = ref<string>('')
const statusMessage = reactive({
  success       : props.successMessage,
  warning       : props.warningMessage,
  commonError   : props.commonErrorMessage,
  fileError     : props.fileErrorMessage,
  fileSizeError : props.fileSizeErrorMessage,
})
const localFields = ref<any[] | null>(null)
const disabled = ref<boolean>(true)
const VALID_INPUTS = ['Input', 'RogueInput', 'Textarea', 'RogueTextarea']

onMounted(() => {
  localFields.value = PERSISTENT_ENTRIES[props.id] || null
  load()
})

async function load(): Promise<void> {
  try {
    fields.value = await rogue.fetch(`/svc_support/zendesk/form/id/${props.id}`, 'get')

    const queryString = Object.keys(rogue.router?.currentRoute.value?.query || {})[0]

    if(rogue.router?.currentRoute.value?.path.startsWith('/console-feedback') && queryString) {
      const queryData = queryString.split('|').map(item => decodeURIComponent(item))
      prePopulateFeedbackForm(queryData)
    }

    // if the user was filling out the form and navigated away, we temporarily save the field values and reload them
    if (localFields.value) {
      localFields.value.forEach(savedValue => {
        const index = fields.value.findIndex(field => field.id === savedValue.id)
        if (index) {
          fields.value[index].value = savedValue.value
        }
      })

      nextTick(() => {
        disabled.value = rootEl.value?.getElementsByClassName ? (rootEl.value.getElementsByClassName('input-error') || []).length > 0 : true
      })
    }

    setActiveFields()
  } catch {
    statusMessage.commonError = 'Failed to load form, please try again later'
    status.value = 'commonError'
  }
}

function removeFile(index: number, id: number): void {
  const fileFields = fields.value.filter((field) => (field.id === id))[0].files
  fileFields?.splice(index, 1)

  setActiveFields()
}

// Recursive function that iterates over the returned spa structure from m2-support
function findChildFields(field: FormField): FormField[] {
  const found: FormField[] = []
  // does this field have conditional fields depending on its value?
  if (!field.value || (field.conditions || []).length === 0) {
    return []
  }

  // based on the current value of the parent field, do we have any conditions that we need to account for?
  const foundConditions = field.conditions?.find(condition => condition.value === field.value)

  if (!foundConditions || (foundConditions.child_fields || []).length === 0) {
    return []
  }

  // what fields need displayed based off of the met parental condition?
  foundConditions.child_fields?.forEach(value => {
    const foundField = fields.value.find(activeChildField => activeChildField.id === value.id)
    if (foundField) {
      found.push(foundField, ...findChildFields(foundField))
    }
  })

  return found
}

function setActiveFields(): void {
  const baseFields = fields.value.filter(value => !value.isChild)
  activeFields.value = []
  activeFields.value.push(...baseFields)
  baseFields.forEach(value => {
    const childFields = findChildFields(value)

    if (childFields.length > 0) {
      activeFields.value.push(...childFields)
    }
  })
  activeFields.value.sort((a, b) => (a.position || 0) - (b.position || 0))
  PERSISTENT_ENTRIES[props.id] = activeFields.value.map(field => ({ id: field.id, value: field.value }))
}

function attachFile(file: File, field: FormField): void {
  if (file.size && file.size <= 25000000) {
    updateFieldValue(field, {
      name : file.name,
      type : file.type,
      size : file.size,
      file,
    })
  } else {
    status.value = 'fileSizeError'
  }
}

function updateFieldValue(field: FormField, value?: string | boolean | ZendeskFile): void {
  if (field.component === 'FileUpload') {
    if (Array.isArray(field.files)) {
      field.files.push(value as ZendeskFile)
    } else {
      field.files = [value as ZendeskFile]
    }
  } else {
    field.value = value as string | boolean | undefined
  }

  setActiveFields()

  nextTick(() => {
    disabled.value = (rootEl.value?.getElementsByClassName('input-error') || []).length > 0
  })
}

async function submit(): Promise<void> {
  status.value = ''

  loading.value = true

  const formData = new FormData()

  fields.value.forEach(field => {
    if (field.files) {
      field.files.forEach((file, i) => {
        formData.append('upload', file.file!, file.name)
      })
    }
  })

  formData.append('formFields', JSON.stringify(fields.value))
  formData.append('ticketFormId', props.id)

  try {
    await rogue.fetchWithCaptcha('/svc_support/zendesk/form/submit', 'POST', formData, { site: rogue.site!.code }, 'json', 'submitZendeskForm')
    if (rogue) {
      rogue.track.track({ type: 'custom', event: 'submit_form', data: { id: props.id } })
    }
  } catch (e: any) {
    if (e.status === 413) {
      status.value = 'fileSizeError'
    } else {
      status.value = 'commonError'
    }
  }
  if (status.value !== 'commonError' && status.value !== 'fileSizeError') {
    status.value = 'success'
    delete PERSISTENT_ENTRIES[props.id]
  }
  loading.value = false
}

// Input, Select, TextArea, And Button components have been renamed with the 
// "Rogue" prefix to differentiate the html element from our custom components.
// This maps those  for old form data that hasn't changed.
function mapComponentName(componentName: string): string {
  if (['Button', 'Input', 'Select', 'Textarea'].includes(componentName)) {
    return `Rogue${componentName}`
  }

  return componentName
}

function prePopulateFeedbackForm(queryData: string[]): void {
  const prePopulateLabels = [
    'Schema Version', 'Firmware Version', 'BLE Version', 'Hardware Version',
    'Battery Level', 'Language (2 digit ISO code)', 'LCD Contrast Level',
    'Lifetime Meters',
  ]

  fields.value.forEach((field) => {
    const dataIndex = prePopulateLabels.indexOf(field.label!)
    if (dataIndex !== -1) {
      const value = queryData[dataIndex]
  
      if (value !== undefined && value !== '') {
        field.value = value
        field.readOnly = true
      }
    }
  })
}
</script>
<style lang='scss' scoped>

.submit {
  margin-top: $space-s;
  margin-bottom: $space-s;
}

.formMessage {
    font-size: 14px;
    margin-bottom: 10px;

    div b {
      font-size: 16px;
    }
  }

  .zendesk-form.main-container :deep() {
    input, select, textarea {
      margin-bottom: 20px;
    }

    .field .label {
        font-size: 14px
    }
  }

</style>
